/**
 * POSLauncherButton — centered POS launcher in the app header.
 *
 * Surfaces inside the header chrome (between ScopeBadge and the right
 * group with NfcStatusIndicator + NotificationsBell) so the agent can
 * jump into a sell workstation in one click without leaving whatever
 * surface they're on.
 *
 * Visibility rules:
 *
 *   1. User must have `canUseWebPOS` permission for the active
 *      location (falls back to `canManagePOS` while the new
 *      `location.use_web_pos` codename is being seeded).
 *   2. POS module must be enabled for the active location.
 *   3. At least one terminal in the active location must have
 *      `device_kind === 'web'`.
 *
 * If any of those three are false the component renders nothing —
 * keeps the header from getting cluttered for users who run their POS
 * on Android/iOS devices only.
 *
 * Click behaviour:
 *
 *   - Single web-enabled terminal → navigates directly to
 *     `/location/pos/overview?terminal=<id>`. The terminal id is a
 *     query param so POSPage can pre-select it without changing the
 *     existing URL grammar.
 *   - Two or more → opens a DropdownMenu listing every web-enabled
 *     terminal with its name + type chip. Pick one → navigate.
 *
 * Mockup: shaka-deck-mockup-commerce-overview.html (V2 — POS launcher
 * in header).
 */

import { ChevronDown, Tablet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLocationUrl } from '@/shared/hooks/useLocationUrl';

import { useTranslation } from '@/core';
import { useAuth } from '@/core/auth/useAuth';
import { canUseWebPOS } from '@/core/auth/modulePermissions';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import {
  useActiveLocationModules,
  isCommercialModuleEnabled,
} from '@/entities/location';
import { useTerminals } from '@/modules/pos/hooks/useTerminal';
import { Button } from '@/theme/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/theme/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const TYPE_GLYPH: Record<string, string> = {
  bar: '🍴',
  restaurant: '🍴',
  shop: '🛍',
  reception: '🛎',
};

// Web POS is still being refined — keep the header launcher hidden in
// production until further notice. It shows in local dev, and in any
// deploy that opts in via VITE_ENABLE_WEB_POS='true' (e.g. staging
// Amplify). Production leaves the var unset, so the launcher never
// mounts there. Staging/prod both build with MODE='production', so the
// only reliable staging↔prod discriminator is this per-deploy env var.
const WEB_POS_ENABLED =
  import.meta.env.DEV ||
  (import.meta.env.VITE_ENABLE_WEB_POS as string | undefined)?.trim() === 'true';

export function POSLauncherButton() {
  // Hidden in production until the web POS is refined (see WEB_POS_ENABLED).
  if (!WEB_POS_ENABLED) {
    return null;
  }

  return <POSLauncherButtonInner />;
}

function POSLauncherButtonInner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locUrl = useLocationUrl();
  const { user } = useAuth();
  const { activeLocationId } = useActiveContext();
  const modules = useActiveLocationModules();

  const allowed = canUseWebPOS(user, activeLocationId);
  const posEnabled = modules === null || isCommercialModuleEnabled(modules, 'pos_commerce');

  // Only fetch terminals when the user could actually launch the POS —
  // otherwise a user without canUseWebPOS 403s on /pos/terminals in the
  // background just from having the header mounted.
  const { data: terminals = [] } = useTerminals(activeLocationId ?? undefined, {
    enabled: allowed && posEnabled,
  });

  // Active web-enabled terminals only — inactive / maintenance ones
  // are not surfaced because the launcher is for "ready-to-go now"
  // flows; admin lives in the Terminals tab.
  const webTerminals = terminals.filter(
    (term) => term.deviceKind === 'web' && term.status === 'active',
  );

  if (!allowed || !posEnabled || webTerminals.length === 0) {
    return null;
  }

  const launchTerminal = (terminalId: string) => {
    navigate(locUrl(`/location/pos/overview?terminal=${terminalId}`));
  };

  // Single web terminal → click goes straight in, no menu.
  if (webTerminals.length === 1) {
    const term = webTerminals[0];
    return (
      <Button
        type="button"
        onClick={() => launchTerminal(term.id)}
        className={cn(
          'h-9 gap-2 rounded-full border border-coral-500 bg-gradient-to-r from-coral-500 to-sun-500 px-4 text-[13px] font-semibold text-white shadow-md shadow-coral-500/30',
          'hover:from-coral-600 hover:to-sun-600 hover:shadow-lg',
        )}
      >
        <Tablet className="h-3.5 w-3.5" />
        <span>{t('posLauncher.openOn', 'Open POS')}</span>
        <span className="font-mono text-[11px] opacity-90">· {term.name}</span>
      </Button>
    );
  }

  // Two or more → dropdown chooser.
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          className={cn(
            'h-9 gap-2 rounded-full border border-coral-500 bg-gradient-to-r from-coral-500 to-sun-500 px-4 text-[13px] font-semibold text-white shadow-md shadow-coral-500/30',
            'hover:from-coral-600 hover:to-sun-600 hover:shadow-lg',
          )}
        >
          <Tablet className="h-3.5 w-3.5" />
          <span>{t('posLauncher.openPos', 'Open POS')}</span>
          <span className="rounded-full bg-white/25 px-1.5 py-0.5 font-mono text-[10px]">
            {webTerminals.length}
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-90" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="min-w-[260px]">
        <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-wide">
          {t('posLauncher.chooseStation', 'Choose a station')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {webTerminals.map((term) => (
          <DropdownMenuItem
            key={term.id}
            onClick={() => launchTerminal(term.id)}
            className="gap-3 py-2.5"
          >
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-coral-100 text-[14px]">
              {TYPE_GLYPH[term.type ?? 'restaurant'] ?? '🖥'}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-sm font-medium text-sand-900">{term.name}</span>
              {term.type && (
                <span className="font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground">
                  {t(`pos.terminalType.${term.type}`, term.type)}
                </span>
              )}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
